import React, { useState, useCallback, useMemo } from 'react';
import api from "../services/api";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";
import useToast from "../hooks/useToast";

// =========================================================================
// MAIN COMPONENT
// =========================================================================
const AssignEditors = () => {
  const { showSuccess, showError } = useToast();
  
  // Modal Visibility States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeAssignEditor, setActiveAssignEditor] = useState(null);

  // Form State
  const [newEditor, setNewEditor] = useState({ name: "", email: "", password: "" });

  // ✅ React Query: Fetch events and users
  const {
    data: events = [],
    isLoading: eventsLoading,
    refetch: refetchEvents,
  } = useApiQuery(
    ['my-events'],
    () => api.get("/events/my-events").then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError("Failed to load events"),
    }
  );

  const {
    data: allUsers = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery(
    ['users'],
    () => api.get("/auth/users").then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError("Failed to load users"),
    }
  );

  // ✅ Filter editors from users
  const editors = useMemo(() => {
    return allUsers.filter((u) => u.role === "event_editor");
  }, [allUsers]);

  const isLoading = eventsLoading || usersLoading;

  // ✅ React Query: Create editor mutation
  const createEditorMutation = useApiMutation(
    (data) => api.post("/auth/register", data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Editor created");
        setShowCreateModal(false);
        setNewEditor({ name: "", email: "", password: "" });
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Creation failed");
      },
    }
  );

  // ✅ React Query: Delete editor mutation
  const deleteEditorMutation = useApiMutation(
    (id) => api.delete(`/auth/users/${id}`).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Editor deleted");
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Delete failed");
      },
    }
  );

  // ✅ React Query: Assign events mutation
  const assignEventsMutation = useApiMutation(
    ({ userId, eventIds }) => 
      api.post("/events/assign-to-user", { userId, eventIds }).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess("Events updated successfully");
        setActiveAssignEditor(null);
        refetchUsers();
        refetchEvents();
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Assignment failed");
      },
    }
  );

  // ✅ Handlers
  const handleCreateEditor = useCallback((e) => {
    e.preventDefault();
    if (!newEditor.name || !newEditor.email || !newEditor.password) {
      showError("All fields required");
      return;
    }
    createEditorMutation.mutate({ ...newEditor, role: "event_editor" });
  }, [newEditor, createEditorMutation, showError]);

  const handleDeleteEditor = useCallback((id, name) => {
    if (!window.confirm(`Delete "${name}"? This will also remove all event assignments.`)) return;
    deleteEditorMutation.mutate(id);
  }, [deleteEditorMutation]);

  const handleAssignEventsSubmit = useCallback((selectedEventIds) => {
    if (!activeAssignEditor) return;
    assignEventsMutation.mutate({
      userId: activeAssignEditor._id,
      eventIds: selectedEventIds,
    });
  }, [activeAssignEditor, assignEventsMutation]);

  const getEventName = useCallback((eventId) => {
    return events.find((e) => e._id === eventId)?.name || eventId;
  }, [events]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createEditorMutation.isPending || 
                       deleteEditorMutation.isPending || 
                       assignEventsMutation.isPending;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header section */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Event Editor Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={isSubmitting}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition disabled:opacity-50"
        >
          + New Editor
        </button>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Event Editors</h2>
        </div>
        
        {editors.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No editors created yet. Click "+ New Editor" to add.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Events</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {editors.map((editor) => (
                  <EditorRow
                    key={editor._id}
                    editor={editor}
                    getEventName={getEventName}
                    onOpenAssign={() => setActiveAssignEditor(editor)}
                    onDelete={handleDeleteEditor}
                    isDeleting={deleteEditorMutation.isPending && deleteEditorMutation.variables === editor._id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Editor Modal */}
      {showCreateModal && (
        <CreateEditorModal
          newEditor={newEditor}
          setNewEditor={setNewEditor}
          creating={createEditorMutation.isPending}
          onSubmit={handleCreateEditor}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Assign Event Modal */}
      {activeAssignEditor && (
        <AssignEventsModal
          editor={activeAssignEditor}
          allEvents={events}
          onClose={() => setActiveAssignEditor(null)}
          onSubmit={handleAssignEventsSubmit}
          isSubmitting={assignEventsMutation.isPending}
        />
      )}
    </div>
  );
};

// =========================================================================
// SUB-COMPONENTS
// =========================================================================

// 1. Table Row Sub-Component
const EditorRow = ({ editor, getEventName, onOpenAssign, onDelete, isDeleting }) => {
  return (
    <tr className="hover:bg-gray-50/70 transition">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{editor.name}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{editor.email}</td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1.5 max-w-md">
          {editor.managedEvents && editor.managedEvents.length > 0 ? (
            editor.managedEvents.map((eventId) => (
              <div
                key={eventId}
                className="inline-flex items-center bg-gray-100 text-gray-700 rounded-full px-3 py-0.5 text-xs font-medium border border-gray-200 shadow-sm"
              >
                {getEventName(eventId)}
              </div>
            ))
          ) : (
            <span className="text-gray-400 text-xs italic">No events assigned</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
        <button
          onClick={onOpenAssign}
          disabled={isDeleting}
          className="text-green-600 hover:text-green-800 transition disabled:opacity-50"
        >
          {editor.managedEvents?.length > 0 ? "Edit Assignments" : "+ Add Events"}
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="text-red-600 hover:text-red-800 transition disabled:opacity-50"
        >
          {isDeleting ? '...' : 'Delete'}
        </button>
      </td>
    </tr>
  );
};

// 2. Assign Events Modal Component
const AssignEventsModal = ({ editor, allEvents, onClose, onSubmit, isSubmitting }) => {
  const [selectedEventIds, setSelectedEventIds] = useState(editor.managedEvents || []);

  const toggleSelection = (id) => {
    setSelectedEventIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <Modal title={`Assign Events to: ${editor.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Check or uncheck events to manage access permissions for this editor.
        </p>
        
        <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100 bg-gray-50/50">
          {allEvents.map((event) => {
            const isChecked = selectedEventIds.includes(event._id);
            return (
              <label 
                key={event._id} 
                className={`flex items-center space-x-3 px-4 py-2.5 cursor-pointer select-none transition ${isChecked ? 'bg-blue-50/40' : 'hover:bg-gray-100'}`}
              >
                <input
                  type="checkbox"
                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                  checked={isChecked}
                  onChange={() => toggleSelection(event._id)}
                  disabled={isSubmitting}
                />
                <span className="text-sm font-medium text-gray-700">{event.name}</span>
              </label>
            );
          })}
          {allEvents.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400">No events found systemwide.</div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50">
            Cancel
          </button>
          <button 
            type="button" 
            onClick={() => onSubmit(selectedEventIds)} 
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// 3. Create Editor Modal Component
const CreateEditorModal = ({ newEditor, setNewEditor, creating, onSubmit, onClose }) => {
  return (
    <Modal title="Create Event Editor" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Full Name *"
          value={newEditor.name}
          onChange={(e) => setNewEditor({ ...newEditor, name: e.target.value })}
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          required
          disabled={creating}
        />
        <input
          type="email"
          placeholder="Email *"
          value={newEditor.email}
          onChange={(e) => setNewEditor({ ...newEditor, email: e.target.value })}
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          required
          disabled={creating}
        />
        <input
          type="password"
          placeholder="Password *"
          value={newEditor.password}
          onChange={(e) => setNewEditor({ ...newEditor, password: e.target.value })}
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          required
          disabled={creating}
        />
        <div className="flex justify-end gap-2 pt-2 border-t">
          <button type="button" onClick={onClose} disabled={creating} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={creating} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition disabled:opacity-50">
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// 4. Base Generic Modal Layout Shell
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-100 transform transition-all animate-in fade-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center p-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 transition">✕</button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

export default AssignEditors;